import React, { useState, useEffect, useMemo } from "react";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const toTitleCase = (str) =>
  str.toLowerCase().replace(/(^|[\s.])([a-z])/g, (_, sep, c) => sep + c.toUpperCase());

const maxDOB = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 18);
  return d.toISOString().split("T")[0];
})();

const ALLOWED_EXTS   = ["jpg", "jpeg", "png"];
const MAX_FILE_BYTES = 200 * 1024;

const compressImage = (file, maxBytes) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width  = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      let quality = 0.92;
      const attempt = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Compression failed."));
            if (blob.size <= maxBytes || quality < 0.1) return resolve(blob);
            quality = Math.max(quality - 0.08, 0.05);
            attempt();
          },
          "image/jpeg",
          quality
        );
      };
      attempt();
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Cannot read image.")); };
    img.src = url;
  });

const FILE_TOOLTIP = "Allowed: JPG, JPEG, PNG only. Max size 200 KB. Larger images will be auto-compressed.";

const ELECTION_YEARS = Array.from({ length: 2026 - 2000 + 1 }, (_, i) => 2026 - i);

export default function NewMember() {
  const localUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); }
    catch { return null; }
  }, []);

  const moduleId = parseInt(localUser?.moduleId, 10);

  const [meta, setMeta] = useState({ titlesNoSeven: [], titlesAll: [], memberTypes: [] });
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [metaError, setMetaError] = useState("");

  const [form, setForm] = useState({
    NameTitleID:      "",
    MemberName:       "",
    FNameTitleID:     "",
    FName:            "",
    Gender:           "",
    Country:          "91",
    Mobile:           "",
    DOB:              "",
    EMail:            "",
    PermanentAddress: "",
    PermanentPIN:     "",
    PermanentStateID: "",
    PermanentCityID:  "",
    MemberTypeID:     "",
    IDCardNo:         "",
    ElectionTerm:     "",
  });
  const [file,    setFile]    = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (moduleId !== 10) return;
    Promise.all([
      fetch(`${API_ROOT}/api/guesthouse/new-member-form-meta?moduleId=10`).then(r => r.json()),
      fetch(`${API_ROOT}/api/states`).then(r => r.json()),
    ])
      .then(([metaData, statesData]) => {
        if (metaData.success) setMeta(metaData.data);
        else setMetaError(metaData.message || "Failed to load form data.");
        setStates(Array.isArray(statesData) ? statesData : []);
      })
      .catch(() => setMetaError("Failed to load form data."));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.PermanentStateID) { setCities([]); return; }
    fetch(`${API_ROOT}/api/cities?stateId=${form.PermanentStateID}`)
      .then(r => r.json())
      .then(d => setCities(Array.isArray(d) ? d : []))
      .catch(() => setCities([]));
  }, [form.PermanentStateID]);

  const onChange = (e) => {
    const { name, value } = e.target;

    if (name === "MemberName" || name === "FName") {
      // Strip disallowed chars (keep letters, spaces, dots only), then title case
      const cleaned = value.replace(/[^a-zA-Z. ]/g, "");
      setForm(prev => ({ ...prev, [name]: toTitleCase(cleaned) }));
      return;
    }
    if (name === "EMail") {
      setForm(prev => ({ ...prev, EMail: value.toLowerCase() }));
      return;
    }
    if (name === "Mobile") {
      // Digits only, max 10
      const digits = value.replace(/\D/g, "").slice(0, 10);
      setForm(prev => ({ ...prev, Mobile: digits }));
      return;
    }
    if (name === "PermanentPIN") {
      // Digits only, max 6
      const digits = value.replace(/\D/g, "").slice(0, 6);
      setForm(prev => ({ ...prev, PermanentPIN: digits }));
      return;
    }
    if (name === "Country") {
      const digits = value.replace(/\D/g, "");
      setForm(prev => ({ ...prev, Country: digits }));
      return;
    }
    if (name === "PermanentStateID") {
      setForm(prev => ({ ...prev, PermanentStateID: value, PermanentCityID: "" }));
      return;
    }

    setForm(prev => ({ ...prev, [name]: value }));
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0] || null;
    if (!f) { setFile(null); return; }

    const ext = f.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      setError("Only JPG, JPEG, and PNG files are allowed.");
      e.target.value = "";
      setFile(null);
      return;
    }

    setError("");

    if (f.size <= MAX_FILE_BYTES) {
      setFile(f);
      return;
    }

    // Auto-compress oversized image
    try {
      const blob = await compressImage(f, MAX_FILE_BYTES);
      const compressed = new File([blob], f.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
      setFile(compressed);
    } catch {
      setError("Image is too large and could not be compressed. Please use a smaller file.");
      e.target.value = "";
      setFile(null);
    }
  };

  const validate = () => {
    const nameRe = /^[a-zA-Z. ]+$/;

    if (!form.NameTitleID)  return "Please select a title for Member Name.";
    if (!form.MemberName.trim()) return "Member Name is required.";
    if (!nameRe.test(form.MemberName.trim()))
      return "Member Name must contain only letters, spaces, or dots (.).";

    if (!form.FNameTitleID) return "Please select a title for Father's / Husband's Name.";
    if (!form.FName.trim()) return "Father's / Husband's Name is required.";
    if (!nameRe.test(form.FName.trim()))
      return "Father's / Husband's Name must contain only letters, spaces, or dots (.).";

    if (!form.Gender)  return "Please select Gender.";
    if (!form.Country) return "Country Code is required.";

    if (!form.Mobile)              return "Mobile number is required.";
    if (!/^\d{10}$/.test(form.Mobile)) return "Mobile must be exactly 10 digits.";

    if (!form.DOB) return "Date of Birth is required.";
    if (form.DOB > maxDOB)
      return "Member must be at least 18 years old.";

    if (!form.EMail.trim()) return "Email is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.EMail))
      return "Please enter a valid email address.";

    if (!form.PermanentAddress.trim()) return "Permanent Address is required.";

    if (!form.PermanentPIN) return "PIN is required.";
    if (!/^\d{6}$/.test(form.PermanentPIN)) return "PIN must be exactly 6 digits.";

    if (!form.PermanentStateID) return "Please select a State.";
    if (!form.PermanentCityID)  return "Please select a City.";
    if (!form.MemberTypeID)     return "Please select a Member Type.";
    if (!form.IDCardNo.trim())  return "ID Card No is required.";
    if (!form.ElectionTerm)     return "Please select an Election Term.";
    if (!file)                  return "Official ID proof file is required.";

    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");

    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("moduleId",         String(moduleId));
      fd.append("NameTitleID",      form.NameTitleID);
      fd.append("MemberName",       form.MemberName.trim());
      fd.append("FNameTitleID",     form.FNameTitleID);
      fd.append("FName",            form.FName.trim());
      fd.append("Gender",           form.Gender);
      fd.append("Country",          form.Country);
      fd.append("Mobile",           form.Mobile.replace(/^0+/, ""));
      fd.append("DOB",              form.DOB);
      fd.append("EMail",            form.EMail.trim());
      fd.append("PermanentAddress", form.PermanentAddress.trim());
      fd.append("PermanentPIN",     form.PermanentPIN);
      fd.append("PermanentStateID", form.PermanentStateID);
      fd.append("PermanentCityID",  form.PermanentCityID);
      fd.append("MemberTypeID",  form.MemberTypeID);
      fd.append("IDCardNo",      form.IDCardNo.trim());
      fd.append("ElectionTerm",  form.ElectionTerm);
      fd.append("pdf_file",      file);

      const res  = await fetch(`${API_ROOT}/api/guesthouse/new-member`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Registration failed.");

      setSuccess(data.message);
      setForm({
        NameTitleID: "", MemberName: "", FNameTitleID: "", FName: "",
        Gender: "", Country: "91", Mobile: "", DOB: "", EMail: "",
        PermanentAddress: "", PermanentPIN: "", PermanentStateID: "", PermanentCityID: "",
        MemberTypeID: "", IDCardNo: "", ElectionTerm: "",
      });
      setFile(null);
      const fi = document.getElementById("pdf_file_input");
      if (fi) fi.value = "";
    } catch (err) {
      setError(err.message || "Registration failed.");
    } finally {
      setSaving(false);
    }
  };

  if (moduleId !== 10) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">Unauthorized access Or Session Timed out.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <h3 className="ctitle">New Member Registration</h3>

      {metaError && <div className="alert alert-danger">{metaError}</div>}
      {error     && <div className="alert alert-danger">{error}</div>}
      {success   && <div className="alert alert-success">{success}</div>}

      <form onSubmit={onSubmit} noValidate>

        {/* Row 1: NameTitle + MemberName + FNameTitle + FName */}
        <div className="row g-2 mb-2">
          <div className="col-sm-2">
            <label className="form-label mb-1">Title</label>
            <select className="form-select" name="NameTitleID" value={form.NameTitleID} onChange={onChange}>
              <option value="">Select</option>
              {meta.titlesNoSeven.map(t => (
                <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>
              ))}
            </select>
          </div>
          <div className="col-sm-4">
            <label className="form-label mb-1">Member Name</label>
            <input
              className="form-control"
              type="text"
              name="MemberName"
              value={form.MemberName}
              onChange={onChange}
              placeholder="Letters, spaces and dot (.) only"
            />
          </div>
          <div className="col-sm-2">
            <label className="form-label mb-1">Father's Title</label>
            <select className="form-select" name="FNameTitleID" value={form.FNameTitleID} onChange={onChange}>
              <option value="">Select</option>
              {meta.titlesAll.map(t => (
                <option key={t.NameTitleID} value={t.NameTitleID}>{t.NameTitle}</option>
              ))}
            </select>
          </div>
          <div className="col-sm-4">
            <label className="form-label mb-1">Father's / Husband's Name</label>
            <input
              className="form-control"
              type="text"
              name="FName"
              value={form.FName}
              onChange={onChange}
              placeholder="Letters, spaces and dot (.) only"
            />
          </div>
        </div>

        {/* Row 2: Gender + Country + Mobile + DOB + Email */}
        <div className="row g-2 mb-2">
          <div className="col-sm-2">
            <label className="form-label mb-1">Gender</label>
            <select className="form-select" name="Gender" value={form.Gender} onChange={onChange}>
              <option value="">Select</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
              <option value="O">Other</option>
            </select>
          </div>
          <div className="col-sm-2">
            <label className="form-label mb-1">Country Code</label>
            <input
              className="form-control"
              type="text"
              name="Country"
              value={form.Country}
              onChange={onChange}
              maxLength={4}
            />
          </div>
          <div className="col-sm-2">
            <label className="form-label mb-1">Mobile (10 digits)</label>
            <input
              className="form-control"
              type="text"
              name="Mobile"
              value={form.Mobile}
              onChange={onChange}
              maxLength={10}
              placeholder="10 digit number"
            />
          </div>
          <div className="col-sm-2">
            <label className="form-label mb-1">Date of Birth</label>
            <input
              className="form-control"
              type="date"
              name="DOB"
              value={form.DOB}
              onChange={onChange}
              max={maxDOB}
            />
          </div>
          <div className="col-sm-4">
            <label className="form-label mb-1">Email</label>
            <input
              className="form-control"
              type="text"
              name="EMail"
              value={form.EMail}
              onChange={onChange}
              placeholder="email@example.com"
            />
          </div>
        </div>

        {/* Row 3: Address + PIN + State + City */}
        <div className="row g-2 mb-2">
          <div className="col-sm-6">
            <label className="form-label mb-1">Permanent Address</label>
            <textarea className="form-control" name="PermanentAddress" value={form.PermanentAddress} onChange={onChange} rows={2} />
          </div>
          <div className="col-sm-2">
            <label className="form-label mb-1">PIN (6 digits)</label>
            <input
              className="form-control"
              type="text"
              name="PermanentPIN"
              value={form.PermanentPIN}
              onChange={onChange}
              maxLength={6}
              placeholder="6 digit PIN"
            />
          </div>
          <div className="col-sm-2">
            <label className="form-label mb-1">State</label>
            <select className="form-select" name="PermanentStateID" value={form.PermanentStateID} onChange={onChange}>
              <option value="">Select State</option>
              {states.map(s => (
                <option key={s.StateID} value={s.StateID}>{s.State}</option>
              ))}
            </select>
          </div>
          <div className="col-sm-2">
            <label className="form-label mb-1">City</label>
            <select className="form-select" name="PermanentCityID" value={form.PermanentCityID} onChange={onChange}>
              <option value="">Select City</option>
              {cities.map(c => (
                <option key={c.CityID} value={c.CityID}>{c.City}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 4: MemberType + IDCardNo + File */}
        <div className="row g-2 mb-3">
          <div className="col-sm-2">
            <label className="form-label mb-1">Member Type</label>
            <select className="form-select" name="MemberTypeID" value={form.MemberTypeID} onChange={onChange}>
              <option value="">Select</option>
              {meta.memberTypes.map(m => (
                <option key={m.MemberTypeID} value={m.MemberTypeID}>{m.MemberType}</option>
              ))}
            </select>
          </div>
          <div className="col-sm-2">
            <label className="form-label mb-1">ID Card No</label>
            <input className="form-control" type="text" name="IDCardNo" value={form.IDCardNo} onChange={onChange} />
          </div>
          <div className="col-sm-2">
            <label className="form-label mb-1">Election Term</label>
            <select className="form-select" name="ElectionTerm" value={form.ElectionTerm} onChange={onChange}>
              <option value="">Select Year</option>
              {ELECTION_YEARS.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="col-sm-4">
            <label className="form-label mb-1">
              Official ID Proof&nbsp;
              <i
                className="bi bi-info-circle text-primary"
                title={FILE_TOOLTIP}
                style={{ cursor: "help" }}
              ></i>
            </label>
            <input
              id="pdf_file_input"
              className="form-control"
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={onFileChange}
            />
            <small className="text-muted">JPG / JPEG / PNG only, max 200 KB (auto-compressed if larger)</small>
          </div>
        </div>

        {/* Row 5: Save */}
        <div className="row g-2 mb-3">
          <div className="col-sm-12">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

      </form>
    </div>
  );
}
